import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Award, Target, Clock, MapPin, Stethoscope, Briefcase, GraduationCap, Video, Mic, UserCircle } from 'lucide-react';

interface SimulationCardProps {
  agentName: string;
  title: string;
  specialty: string;
  experience: string;
  goalScore: number;
  yourBestScore?: number;
  imageUrl?: string;
  platform?: 'elevenlabs' | 'anam';
  agentType?: 'patient' | 'customer' | 'examiner' | 'coach';
  difficulty?: string;
  sessionsCompleted?: number;
  estimatedDuration?: string;
  location?: string;
  onStart: () => void;
}

const agentTypeConfig = {
  patient: { label: 'Patient', icon: Stethoscope, className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  customer: { label: 'Customer', icon: Briefcase, className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  examiner: { label: 'Examiner', icon: GraduationCap, className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  coach: { label: 'Coach', icon: UserCircle, className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
};

const difficultyConfig: Record<string, string> = {
  beginner: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  intermediate: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  advanced: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  expert: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export function SimulationCard({
  agentName,
  title,
  specialty,
  experience,
  goalScore,
  yourBestScore,
  imageUrl,
  platform,
  agentType,
  difficulty,
  sessionsCompleted = 0,
  estimatedDuration,
  location,
  onStart,
}: SimulationCardProps) {
  const hasAchievedGoal = yourBestScore !== undefined && yourBestScore >= goalScore;
  const progressPercent = yourBestScore !== undefined ? Math.min((yourBestScore / goalScore) * 100, 100) : 0;
  const typeConfig = agentType ? agentTypeConfig[agentType] : null;
  const TypeIcon = typeConfig?.icon;

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm hover:shadow-lg hover:border-primary/20 transition-all duration-300 group">
      {/* Header with Large Avatar and Badges */}
      <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5 pb-6 relative">
        {/* Top badges row */}
        <div className="flex items-start justify-between mb-5">
          {/* Agent Type Badge */}
          {typeConfig && TypeIcon && (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${typeConfig.className}`}>
              <TypeIcon className="w-3 h-3" />
              {typeConfig.label}
            </span>
          )}
          
          {/* Platform Badge */}
          {platform && (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              platform === 'anam' 
                ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' 
                : 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400'
            }`}>
              {platform === 'anam' ? <Video className="w-3 h-3" /> : <Mic className="w-3 h-3" />}
              {platform === 'anam' ? 'Video' : 'Audio'}
            </span>
          )}
        </div>

        {/* Avatar - Larger and more prominent */}
        <div className="flex items-center justify-center">
          <div className="w-28 h-28 rounded-2xl bg-background/80 flex items-center justify-center shadow-lg ring-4 ring-background/50 overflow-hidden group-hover:ring-primary/20 group-hover:shadow-xl transition-all duration-300">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={agentName}
                className="w-28 h-28 rounded-2xl object-cover transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <div className="text-3xl font-bold text-primary">
                {agentName
                  .split(' ')
                  .map(n => n[0])
                  .join('')}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-4">
        {/* Name and Title */}
        <div className="text-center">
          <h3 className="text-xl font-bold text-foreground mb-1 leading-tight">{agentName}</h3>
          <p className="text-sm text-muted-foreground line-clamp-1">{title}</p>
        </div>

        {/* Metadata */}
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {specialty && (
            <span className="flex items-center gap-1">
              <Stethoscope className="w-3 h-3" />
              {specialty}
            </span>
          )}
          {experience && (
            <span className="flex items-center gap-1">
              <Briefcase className="w-3 h-3" />
              {experience}
            </span>
          )}
          {location && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {location}
            </span>
          )}
        </div>

        {/* Difficulty Badge */}
        {difficulty && (
          <div className="flex justify-center">
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${difficultyConfig[difficulty.toLowerCase()] || 'bg-muted text-muted-foreground'}`}>
              {difficulty}
            </span>
          </div>
        )}

        {/* Progress Section */}
        <div className="space-y-2 pt-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground flex items-center gap-1">
              <Target className="w-3 h-3" />
              Goal: {goalScore}%
            </span>
            {yourBestScore !== undefined && (
              <span className={`font-medium flex items-center gap-1 ${hasAchievedGoal ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                <Award className="w-3 h-3" />
                Best: {yourBestScore}%
              </span>
            )}
          </div>
          
          <Progress 
            value={progressPercent} 
            className={`h-2 ${hasAchievedGoal ? '[&>div]:bg-green-500' : '[&>div]:bg-primary'}`}
          />
          
          {hasAchievedGoal && (
            <p className="text-xs text-center text-green-600 dark:text-green-400 font-medium">
              ✓ Goal Achieved!
            </p>
          )}
        </div>

        {/* Session Info */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-border">
          <span className="flex items-center gap-1">
            {sessionsCompleted} session{sessionsCompleted !== 1 ? 's' : ''} completed
          </span>
          {estimatedDuration && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {estimatedDuration}
            </span>
          )}
        </div>

        {/* CTA Button */}
        <Button
          onClick={onStart}
          className="w-full font-semibold"
          variant={hasAchievedGoal ? "outline" : "default"}
        >
          {yourBestScore !== undefined ? 'Continue Training' : 'Start Simulation'}
        </Button>
      </div>
    </div>
  );
}
